// ========================================================
// ADMIN DASHBOARD - SAAS MANAGEMENT INTERFACE
// Complete admin panel for managing customers, revenue, and analytics
// ========================================================

import React, { useState, useEffect } from 'react';
import { 
  Users, 
  CreditCard, 
  BarChart3, 
  Settings, 
  Eye, 
  Trash2, 
  Plus,
  DollarSign,
  TrendingUp,
  Clock,
  Search,
  Download,
  RefreshCw,
  Gift,
  Moon,
  Sun,
  UserPlus,
  Mail,
  UserCog
} from 'lucide-react';
import apiService from '../services/SupabaseApiService';
import SubscriptionOverrideManager from './SubscriptionOverrideManager';
import AdminUserManagement from './AdminUserManagement';
import toast from 'react-hot-toast';

// Use the singleton instance
const ApiService = apiService;

const AdminDashboard = () => {
  const [activeTab, setActiveTab] = useState('overview');
  const [families, setFamilies] = useState([]);
  const [subscriptions, setSubscriptions] = useState([]);
  const [revenue, setRevenue] = useState({});
  const [signupStats, setSignupStats] = useState({ signups: [], counts: { last24h: 0, last7d: 0, last30d: 0 } });
  const [pendingInvites, setPendingInvites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedFamily, setSelectedFamily] = useState(null);
  const [showFamilyModal, setShowFamilyModal] = useState(false);
  const [showCreateFamilyModal, setShowCreateFamilyModal] = useState(false);
  const [showCreateAdminModal, setShowCreateAdminModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [isDarkMode, setIsDarkMode] = useState(() => {
    const saved = localStorage.getItem('darkMode');
    return saved ? JSON.parse(saved) : false;
  });

  // Load admin data
  useEffect(() => {
    loadAdminData();
  }, []);

  // Handle dark mode
  useEffect(() => {
    if (isDarkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
    localStorage.setItem('darkMode', JSON.stringify(isDarkMode));
  }, [isDarkMode]);

  const toggleDarkMode = () => {
    setIsDarkMode(!isDarkMode);
  };

  const loadAdminData = async () => {
    try {
      setLoading(true);
      
      const [familiesData, subscriptionsData, revenueData, recentSignups, pending] = await Promise.all([
        ApiService.getAllFamilies(),
        ApiService.getAllSubscriptions(),
        ApiService.getRevenueMetrics(),
        ApiService.getRecentSignups({ days: 30, limit: 100 }),
        ApiService.getPendingInvitations({ limit: 50 }),
      ]);

      setFamilies(familiesData || []);
      setSubscriptions(subscriptionsData || []);
      setRevenue(revenueData || {});
      setSignupStats(recentSignups || { signups: [], counts: { last24h: 0, last7d: 0, last30d: 0 } });
      setPendingInvites(pending || []);
    } catch (error) {
      console.error('Failed to load admin data:', error);
      toast.error('Failed to load admin data');
    } finally {
      setLoading(false);
    }
  };

  // Filter families based on search and status
  const filteredFamilies = families.filter(family => {
    const matchesSearch = family.family_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         family.parent_email?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = filterStatus === 'all' || family.subscription_status === filterStatus;
    return matchesSearch && matchesStatus;
  });

  // Calculate metrics
  const metrics = {
    totalFamilies: families.length,
    activeSubscriptions: subscriptions.filter(s => s.status === 'active').length,
    monthlyRevenue: revenue.monthlyRevenue || 0,
    totalRevenue: revenue.totalRevenue || 0,
    churnRate: revenue.churnRate || 0,
    avgRevenuePerFamily: revenue.avgRevenuePerFamily || 0
  };

  const handleFamilyAction = async (familyId, action, data = {}) => {
    try {
      switch (action) {
        case 'suspend':
          await ApiService.suspendFamily(familyId);
          break;
        case 'activate':
          await ApiService.activateFamily(familyId);
          break;
        case 'updateSubscription':
          await ApiService.updateFamilySubscription(familyId, data);
          break;
        case 'delete':
          if (window.confirm('Are you sure you want to delete this family? This action cannot be undone.')) {
            await ApiService.deleteFamily(familyId);
          }
          break;
      }
      loadAdminData(); // Refresh data
      toast.success(`Family ${action} complete`);
    } catch (error) {
      console.error(`Failed to ${action} family:`, error);
      toast.error(error.message || `Failed to ${action} family`);
    }
  };

  const exportData = (type) => {
    const data = type === 'families' ? families : subscriptions;
    const csv = convertToCSV(data);
    downloadCSV(csv, `${type}_export_${new Date().toISOString().split('T')[0]}.csv`);
  };

  const convertToCSV = (data) => {
    if (!data.length) return '';
    const headers = Object.keys(data[0]);
    const csvData = [headers.join(','), ...data.map(row => headers.map(header => JSON.stringify(row[header] || '')).join(','))];
    return csvData.join('\n');
  };

  const downloadCSV = (csv, filename) => {
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    if (link.download !== undefined) {
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', filename);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading admin dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">TrophyTime Admin</h1>
              <p className="text-gray-600 dark:text-gray-400">Manage your SAAS platform</p>
            </div>
            <div className="flex space-x-3">
              <button 
                onClick={toggleDarkMode}
                className="inline-flex items-center px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700"
                title={isDarkMode ? 'Switch to light mode' : 'Switch to dark mode'}
              >
                {isDarkMode ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
              </button>
              <button 
                onClick={() => exportData('families')}
                className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700"
              >
                <Download className="h-4 w-4 mr-2" />
                Export Families
              </button>
              <button 
                onClick={loadAdminData}
                className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
              >
                <RefreshCw className="h-4 w-4 mr-2" />
                Refresh
              </button>
            </div>
          </div>

          {/* Tabs */}
          <nav className="flex space-x-8">
            {[
              { id: 'overview', label: 'Overview', icon: BarChart3 },
              { id: 'users', label: 'Users', icon: UserCog },
              { id: 'signups', label: 'Signups', icon: UserPlus },
              { id: 'families', label: 'Families', icon: Users },
              { id: 'subscriptions', label: 'Subscriptions', icon: CreditCard },
              { id: 'overrides', label: 'Overrides', icon: Gift },
              { id: 'revenue', label: 'Revenue', icon: DollarSign },
              { id: 'settings', label: 'Settings', icon: Settings }
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center py-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === tab.id
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <tab.icon className="h-5 w-5 mr-2" />
                {tab.label}
              </button>
            ))}
          </nav>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {activeTab === 'overview' && (
          <OverviewTab 
            metrics={metrics} 
            signupCounts={signupStats.counts}
            setShowCreateFamilyModal={setShowCreateFamilyModal}
            setShowCreateAdminModal={setShowCreateAdminModal}
            setActiveTab={setActiveTab}
          />
        )}
        {activeTab === 'users' && <AdminUserManagement />}
        {activeTab === 'signups' && (
          <SignupsTab
            signupStats={signupStats}
            pendingInvites={pendingInvites}
            onFamilyAction={handleFamilyAction}
            onRefresh={loadAdminData}
          />
        )}
        {activeTab === 'families' && (
          <FamiliesTab
            families={filteredFamilies}
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            filterStatus={filterStatus}
            setFilterStatus={setFilterStatus}
            onFamilyAction={handleFamilyAction}
            selectedFamily={selectedFamily}
            setSelectedFamily={setSelectedFamily}
            showFamilyModal={showFamilyModal}
            setShowFamilyModal={setShowFamilyModal}
          />
        )}
        {activeTab === 'subscriptions' && (
          <SubscriptionsTab subscriptions={subscriptions} />
        )}
        {activeTab === 'overrides' && <SubscriptionOverrideManager />}
        {activeTab === 'revenue' && <RevenueTab revenue={revenue} />}
        {activeTab === 'settings' && <SettingsTab />}
      </div>

      {/* Modals */}
      {showFamilyModal && selectedFamily && (
        <FamilyDetailModal
          family={selectedFamily}
          onClose={() => {
            setShowFamilyModal(false);
            setSelectedFamily(null);
          }}
          onFamilyAction={handleFamilyAction}
        />
      )}

      {showCreateAdminModal && (
        <CreateAdminModal
          onClose={() => setShowCreateAdminModal(false)}
          onAdminCreated={() => {
            setShowCreateAdminModal(false);
            loadAdminData();
          }}
          ApiService={ApiService}
        />
      )}

      {showCreateFamilyModal && (
        <CreateFamilyModal
          onClose={() => setShowCreateFamilyModal(false)}
          onFamilyCreated={() => {
            setShowCreateFamilyModal(false);
            loadAdminData();
          }}
        />
      )}
    </div>
  );
};

// Overview Tab Component
const OverviewTab = ({ metrics, signupCounts = {}, setShowCreateFamilyModal, setShowCreateAdminModal, setActiveTab }) => (
  <div className="space-y-6">
    {/* Metrics Cards */}
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
      <MetricCard
        title="Total Families"
        value={metrics.totalFamilies}
        icon={Users}
        color="blue"
      />
      <MetricCard
        title="Signups (24h)"
        value={signupCounts.last24h || 0}
        icon={UserPlus}
        color="green"
      />
      <MetricCard
        title="Signups (7d)"
        value={signupCounts.last7d || 0}
        icon={Clock}
        color="purple"
      />
      <MetricCard
        title="Active Subscriptions"
        value={metrics.activeSubscriptions}
        icon={CreditCard}
        color="green"
      />
    </div>

    {/* Quick Actions */}
    <div className="bg-white rounded-lg shadow p-6">
      <h3 className="text-lg font-medium text-gray-900 mb-4">Quick Actions</h3>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <button 
          onClick={() => setActiveTab('signups')}
          className="flex items-center p-4 border border-gray-200 rounded-lg hover:bg-gray-50"
        >
          <UserPlus className="h-5 w-5 text-blue-600 mr-3" />
          <div className="text-left">
            <p className="font-medium text-gray-900">Review Signups</p>
            <p className="text-sm text-gray-500">Watch for spam / fake accounts</p>
          </div>
        </button>
        <button 
          onClick={() => setShowCreateAdminModal(true)}
          className="flex items-center p-4 border border-gray-200 rounded-lg hover:bg-gray-50"
        >
          <Users className="h-5 w-5 text-green-600 mr-3" />
          <div className="text-left">
            <p className="font-medium text-gray-900">Add Admin</p>
            <p className="text-sm text-gray-500">Create new admin account</p>
          </div>
        </button>
        <button 
          onClick={() => setActiveTab('families')}
          className="flex items-center p-4 border border-gray-200 rounded-lg hover:bg-gray-50"
        >
          <BarChart3 className="h-5 w-5 text-purple-600 mr-3" />
          <div className="text-left">
            <p className="font-medium text-gray-900">Manage Families</p>
            <p className="text-sm text-gray-500">Suspend or delete accounts</p>
          </div>
        </button>
        <button 
          onClick={() => setActiveTab('users')}
          className="flex items-center p-4 border border-gray-200 rounded-lg hover:bg-gray-50"
        >
          <UserCog className="h-5 w-5 text-indigo-600 mr-3" />
          <div className="text-left">
            <p className="font-medium text-gray-900">User Management</p>
            <p className="text-sm text-gray-500">Auth users, invites, purge emails</p>
          </div>
        </button>
      </div>
    </div>
  </div>
);

// Signups integrity tab
const SignupsTab = ({ signupStats, pendingInvites, onFamilyAction, onRefresh }) => {
  const signups = signupStats?.signups || [];
  const counts = signupStats?.counts || { last24h: 0, last7d: 0, last30d: 0 };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-slate-900">Recent Signups</h2>
          <p className="text-sm text-slate-600">Monitor new families for spam or fake accounts</p>
        </div>
        <button
          type="button"
          onClick={onRefresh}
          className="inline-flex items-center gap-2 rounded-md border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
        >
          <RefreshCw className="h-4 w-4" />
          Refresh
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="rounded-lg border border-slate-200 bg-white p-4">
          <p className="text-xs font-medium uppercase tracking-wide text-slate-500">Last 24 hours</p>
          <p className="mt-1 text-3xl font-bold text-slate-900">{counts.last24h}</p>
        </div>
        <div className="rounded-lg border border-slate-200 bg-white p-4">
          <p className="text-xs font-medium uppercase tracking-wide text-slate-500">Last 7 days</p>
          <p className="mt-1 text-3xl font-bold text-slate-900">{counts.last7d}</p>
        </div>
        <div className="rounded-lg border border-slate-200 bg-white p-4">
          <p className="text-xs font-medium uppercase tracking-wide text-slate-500">Last 30 days</p>
          <p className="mt-1 text-3xl font-bold text-slate-900">{counts.last30d}</p>
        </div>
      </div>

      <div className="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm">
        <div className="border-b border-slate-200 px-6 py-4">
          <h3 className="font-medium text-slate-900">New families</h3>
        </div>
        {signups.length === 0 ? (
          <p className="px-6 py-8 text-sm text-slate-500">No family signups in the last 30 days.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-slate-200">
              <thead className="bg-slate-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-slate-500">When</th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-slate-500">Family</th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-slate-500">Parent</th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-slate-500">Email</th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-slate-500">Plan</th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-slate-500">Status</th>
                  <th className="px-6 py-3 text-right text-xs font-medium uppercase tracking-wider text-slate-500">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200 bg-white">
                {signups.map((family) => (
                  <tr key={family.id} className="hover:bg-slate-50">
                    <td className="whitespace-nowrap px-6 py-4 text-sm text-slate-600">
                      {family.created_at ? new Date(family.created_at).toLocaleString() : '—'}
                    </td>
                    <td className="whitespace-nowrap px-6 py-4 text-sm font-medium text-slate-900">
                      {family.family_name || '—'}
                    </td>
                    <td className="whitespace-nowrap px-6 py-4 text-sm text-slate-700">
                      {family.parent_name || '—'}
                    </td>
                    <td className="whitespace-nowrap px-6 py-4 text-sm text-slate-700">
                      {family.parent_email || '—'}
                    </td>
                    <td className="whitespace-nowrap px-6 py-4 text-sm text-slate-700">
                      {(family.subscription_tier || 'free').toUpperCase()}
                    </td>
                    <td className="whitespace-nowrap px-6 py-4">
                      <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-semibold ${
                        family.subscription_status === 'active'
                          ? 'bg-emerald-100 text-emerald-800'
                          : 'bg-amber-100 text-amber-800'
                      }`}>
                        {family.subscription_status || 'unknown'}
                      </span>
                    </td>
                    <td className="whitespace-nowrap px-6 py-4 text-right text-sm">
                      <button
                        type="button"
                        onClick={() => onFamilyAction(family.id, 'suspend')}
                        className="mr-2 text-amber-700 hover:underline"
                      >
                        Suspend
                      </button>
                      <button
                        type="button"
                        onClick={() => onFamilyAction(family.id, 'delete')}
                        className="text-red-700 hover:underline"
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm">
        <div className="border-b border-slate-200 px-6 py-4">
          <h3 className="font-medium text-slate-900">Pending invitations</h3>
          <p className="text-xs text-slate-500">Invites waiting to be accepted (run admin-family-members-rls.sql if this is empty while you know invites exist)</p>
        </div>
        {pendingInvites.length === 0 ? (
          <p className="px-6 py-8 text-sm text-slate-500">No pending invitations visible.</p>
        ) : (
          <ul className="divide-y divide-slate-200">
            {pendingInvites.map((invite) => (
              <li key={invite.id} className="flex items-center justify-between gap-4 px-6 py-4">
                <div className="min-w-0">
                  <p className="truncate text-sm font-medium text-slate-900">
                    {invite.email}
                  </p>
                  <p className="text-xs text-slate-500">
                    {(invite.first_name || '')} {(invite.last_name || '')} · {invite.role}
                    {invite.families?.family_name ? ` · ${invite.families.family_name}` : ''}
                  </p>
                </div>
                <div className="flex items-center gap-2 text-xs text-slate-500">
                  <Mail className="h-3.5 w-3.5" />
                  {invite.created_at ? new Date(invite.created_at).toLocaleDateString() : '—'}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

// Families Tab Component
const FamiliesTab = ({
  families,
  searchTerm,
  setSearchTerm,
  filterStatus,
  setFilterStatus,
  onFamilyAction,
  selectedFamily,
  setSelectedFamily,
  showFamilyModal,
  setShowFamilyModal
}) => (
  <div className="space-y-6">
    {/* Search and Filter */}
    <div className="bg-white rounded-lg shadow p-6">
      <div className="flex justify-between items-center">
        <div className="relative flex-1 max-w-lg">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
          <input
            type="text"
            placeholder="Search families..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border-2 border-slate-300 bg-slate-50 text-slate-900 rounded-md focus:ring-2 focus:ring-blue-500/30 focus:border-blue-600 focus:bg-white"
          />
        </div>
        <div className="flex gap-2">
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="px-3 py-2 border-2 border-slate-300 bg-slate-50 text-slate-900 rounded-md focus:ring-2 focus:ring-blue-500/30 focus:border-blue-600"
          >
            <option value="all">All Status</option>
            <option value="active">Active</option>
            <option value="past_due">Past Due</option>
            <option value="canceled">Canceled</option>
            <option value="trialing">Trialing</option>
          </select>
        </div>
      </div>
    </div>

    {/* Families Table */}
    <div className="bg-white rounded-lg shadow overflow-hidden">
      <div className="px-6 py-4 border-b border-gray-200">
        <h3 className="text-lg font-medium text-gray-900">Families ({families.length})</h3>
      </div>
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Family
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Subscription
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Status
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Created
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {families.map((family) => (
              <tr key={family.id} className="hover:bg-gray-50">
                <td className="px-6 py-4 whitespace-nowrap">
                  <div>
                    <div className="text-sm font-medium text-gray-900">{family.family_name}</div>
                    <div className="text-sm text-gray-500">{family.parent_email}</div>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className="text-sm text-gray-900">{family.subscription_tier?.toUpperCase() || 'FREE'}</span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                    family.subscription_status === 'active' ? 'bg-green-100 text-green-800' :
                    family.subscription_status === 'past_due' ? 'bg-yellow-100 text-yellow-800' :
                    'bg-red-100 text-red-800'
                  }`}>
                    {family.subscription_status?.toUpperCase() || 'UNKNOWN'}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {family.created_at ? new Date(family.created_at).toLocaleDateString() : 'N/A'}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                  <div className="flex space-x-2">
                    <button
                      onClick={() => {
                        setSelectedFamily(family);
                        setShowFamilyModal(true);
                      }}
                      className="text-blue-600 hover:text-blue-900"
                    >
                      <Eye className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => onFamilyAction(family.id, 'suspend')}
                      className="text-yellow-600 hover:text-yellow-900"
                    >
                      <Clock className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => onFamilyAction(family.id, 'delete')}
                      className="text-red-600 hover:text-red-900"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  </div>
);

// Subscriptions Tab Component
const SubscriptionsTab = ({ subscriptions }) => (
  <div className="space-y-6">
    <div className="bg-white rounded-lg shadow overflow-hidden">
      <div className="px-6 py-4 border-b border-gray-200">
        <h3 className="text-lg font-medium text-gray-900">Active Subscriptions ({subscriptions.length})</h3>
      </div>
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Family
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Plan
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Amount
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Status
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Next Billing
            </th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {subscriptions.map((subscription) => (
            <tr key={subscription.id} className="hover:bg-gray-50">
              <td className="px-6 py-4 whitespace-nowrap">
                <div className="text-sm font-medium text-gray-900">{subscription.family_name}</div>
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                <span className="text-sm text-gray-900">{subscription.plan_name}</span>
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                <span className="text-sm text-gray-900">${subscription.amount}</span>
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                  subscription.status === 'active' ? 'bg-green-100 text-green-800' :
                  subscription.status === 'past_due' ? 'bg-yellow-100 text-yellow-800' :
                  'bg-red-100 text-red-800'
                }`}>
                  {subscription.status?.toUpperCase()}
                </span>
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                {subscription.next_billing_date ? new Date(subscription.next_billing_date).toLocaleDateString() : 'N/A'}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </div>
);

// Revenue Tab Component
const RevenueTab = ({ revenue }) => (
  <div className="space-y-6">
    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Monthly Revenue</h3>
        <p className="text-3xl font-bold text-green-600">${revenue.monthlyRevenue?.toLocaleString() || 0}</p>
      </div>
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Total Revenue</h3>
        <p className="text-3xl font-bold text-blue-600">${revenue.totalRevenue?.toLocaleString() || 0}</p>
      </div>
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Churn Rate</h3>
        <p className="text-3xl font-bold text-red-600">{(revenue.churnRate || 0).toFixed(1)}%</p>
      </div>
    </div>
    
    <div className="bg-white rounded-lg shadow p-6">
      <h3 className="text-lg font-medium text-gray-900 mb-4">Revenue Chart</h3>
      <div className="h-64 bg-gray-100 rounded flex items-center justify-center">
        <p className="text-gray-500">Revenue chart will be implemented here</p>
      </div>
    </div>
  </div>
);

// Settings Tab Component
const SettingsTab = () => (
  <div className="space-y-6">
    <div className="bg-white rounded-lg shadow p-6">
      <h3 className="text-lg font-medium text-gray-900 mb-4">System Settings</h3>
      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700">Stripe Webhook URL</label>
          <input
            type="text"
            className="mt-1 block w-full border-2 border-slate-300 bg-slate-50 text-slate-900 rounded-md px-3 py-2 focus:border-blue-600 focus:bg-white"
            placeholder="https://your-domain.com/api/webhooks/stripe"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700">Email Service</label>
          <select className="mt-1 block w-full border-2 border-slate-300 bg-slate-50 text-slate-900 rounded-md px-3 py-2 focus:border-blue-600 focus:bg-white">
            <option>SendGrid</option>
            <option>Mailgun</option>
            <option>SMTP</option>
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700">Default Plan</label>
          <select className="mt-1 block w-full border-2 border-slate-300 bg-slate-50 text-slate-900 rounded-md px-3 py-2 focus:border-blue-600 focus:bg-white">
            <option>Free</option>
            <option>Premium</option>
            <option>Family+</option>
          </select>
        </div>
      </div>
    </div>
  </div>
);

// Family Detail Modal Component
const FamilyDetailModal = ({ family, onClose, onFamilyAction }) => {
  const [familyMembers, setFamilyMembers] = useState([]);
  const [loadingMembers, setLoadingMembers] = useState(true);

  useEffect(() => {
    loadFamilyMembers();
  }, [family.id]);

  const loadFamilyMembers = async () => {
    try {
      setLoadingMembers(true);
      const members = await ApiService.getFamilyMembers(family.id);
      setFamilyMembers(members);
    } catch (error) {
      console.error('Failed to load family members:', error);
      setFamilyMembers([]);
    } finally {
      setLoadingMembers(false);
    }
  };

  // Family member addition moved to client dashboard

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-10 mx-auto p-6 border w-4/5 max-w-4xl shadow-lg rounded-md bg-white">
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-xl font-medium text-gray-900">Family Management: {family.family_name}</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <Plus className="h-6 w-6 rotate-45" />
          </button>
        </div>
        
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Family Basic Info */}
          <div className="space-y-4">
            <h4 className="text-lg font-medium text-gray-900">Family Information</h4>
            <div className="bg-gray-50 p-4 rounded-lg space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700">Family Name</label>
                <p className="mt-1 text-sm text-gray-900">{family.family_name}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Primary Parent Email</label>
                <p className="mt-1 text-sm text-gray-900">{family.parent_email}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Subscription</label>
                <p className="mt-1 text-sm text-gray-900">
                  <span className="font-semibold">{family.subscription_tier?.toUpperCase() || 'FREE'}</span>
                  <span className={`ml-2 px-2 py-1 text-xs rounded-full ${
                    family.subscription_status === 'active' ? 'bg-green-100 text-green-800' :
                    family.subscription_status === 'past_due' ? 'bg-yellow-100 text-yellow-800' :
                    'bg-red-100 text-red-800'
                  }`}>
                    {family.subscription_status?.toUpperCase() || 'UNKNOWN'}
                  </span>
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Created</label>
                <p className="mt-1 text-sm text-gray-900">
                  {family.created_at ? new Date(family.created_at).toLocaleDateString() : 'N/A'}
                </p>
              </div>
            </div>
          </div>

          {/* Family Members */}
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <h4 className="text-lg font-medium text-gray-900">Family Members</h4>
              <div className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">
                Admin View Only
              </div>
            </div>
            
            <div className="bg-gray-50 p-4 rounded-lg max-h-64 overflow-y-auto">
              {loadingMembers ? (
                <div className="text-center py-4">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mx-auto"></div>
                  <p className="text-sm text-gray-500 mt-2">Loading family members...</p>
                </div>
              ) : familyMembers.length === 0 ? (
                                 <div className="text-center py-4">
                   <Users className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                   <p className="text-sm text-gray-500">No additional family members</p>
                   <p className="text-xs text-gray-400">Family can add members through their dashboard</p>
                 </div>
              ) : (
                <div className="space-y-3">
                  {familyMembers.map((member) => (
                    <div key={member.id} className="flex items-center justify-between bg-white p-3 rounded border">
                      <div>
                        <p className="font-medium text-gray-900">{member.name}</p>
                        <p className="text-sm text-gray-500">{member.email}</p>
                        <span className={`inline-block px-2 py-1 text-xs rounded-full ${
                          member.role === 'primary_parent' ? 'bg-blue-100 text-blue-800' :
                          member.role === 'secondary_parent' ? 'bg-green-100 text-green-800' :
                          member.role === 'grandparent' ? 'bg-purple-100 text-purple-800' :
                          member.role === 'caregiver' ? 'bg-orange-100 text-orange-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {member.role?.replace('_', ' ').toUpperCase() || 'MEMBER'}
                        </span>
                      </div>
                      <div className="text-xs text-gray-400">
                        {member.joined_at ? 'Active' : 'Pending'}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
        
        {/* Actions */}
        <div className="flex justify-end space-x-3 pt-6 border-t mt-6">
          <button
            onClick={() => onFamilyAction(family.id, 'activate')}
            className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
          >
            Activate Family
          </button>
          <button
            onClick={() => onFamilyAction(family.id, 'suspend')}
            className="px-4 py-2 bg-yellow-600 text-white rounded-md hover:bg-yellow-700"
          >
            Suspend Family
          </button>
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400"
          >
            Close
          </button>
        </div>

        {/* Note: Family member addition moved to client dashboard */}
      </div>
    </div>
  );
};

// Family member addition functionality moved to client dashboard (FamilyMemberManagement.js)

// Metric Card Component
const MetricCard = ({ title, value, icon: Icon, color }) => {
  const colorClasses = {
    blue: 'bg-blue-500',
    green: 'bg-green-500',
    purple: 'bg-purple-500',
    orange: 'bg-orange-500',
    red: 'bg-red-500'
  };

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <div className="flex items-center">
        <div className={`flex-shrink-0 p-3 rounded-md ${colorClasses[color]}`}>
          <Icon className="h-6 w-6 text-white" />
        </div>
        <div className="ml-5 w-0 flex-1">
          <dl>
            <dt className="text-sm font-medium text-gray-500 truncate">{title}</dt>
            <dd className="text-lg font-medium text-gray-900">{value}</dd>
          </dl>
        </div>
      </div>
    </div>
  );
};

// Create Admin Modal Component
const CreateAdminModal = ({ onClose, onAdminCreated, ApiService }) => {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    fullName: '',
    confirmPassword: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    // Validation
    if (!formData.fullName.trim()) {
      setError('Full name is required');
      setLoading(false);
      return;
    }

    if (!formData.email.trim()) {
      setError('Email is required');
      setLoading(false);
      return;
    }

    if (formData.password.length < 6) {
      setError('Password must be at least 6 characters long');
      setLoading(false);
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      setLoading(false);
      return;
    }

    try {
      console.log('🔄 Submitting admin creation form...');
      
      const result = await ApiService.createAdminAccount(
        formData.email.trim(),
        formData.password,
        formData.fullName.trim()
      );

      console.log('📝 Admin creation result:', result);

      if (result.success) {
        if (result.needsConfirmation) {
          // Email confirmation required
          console.log('📧 Admin account created, email confirmation required');
          setError(''); // Clear any previous errors
          alert(`✅ Admin account created successfully!\n\n📧 Please check ${result.email} and click the confirmation link to activate the account.\n\nThe account will be fully functional after email confirmation.`);
          onAdminCreated(); // Close modal and refresh
        } else {
          // Account fully created and confirmed
          console.log('✅ Admin account created and confirmed!');
          setError('');
          setTimeout(() => {
            onAdminCreated();
          }, 500);
        }
      } else {
        console.error('❌ Admin creation failed:', result.error);
        setError(result.error || 'Failed to create admin account');
      }
    } catch (error) {
      console.error('❌ Unexpected error during admin creation:', error);
      setError(error.message || 'An unexpected error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
        <div className="mt-3">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Create New Admin Account</h3>
          
          {error && (
            <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">
              {error}
            </div>
          )}
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">Full Name</label>
              <input
                type="text"
                required
                value={formData.fullName}
                onChange={(e) => setFormData({...formData, fullName: e.target.value})}
                className="mt-1 block w-full border-2 border-slate-300 bg-slate-50 text-slate-900 rounded-md px-3 py-2 focus:border-blue-600 focus:bg-white"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700">Email</label>
              <input
                type="email"
                required
                value={formData.email}
                onChange={(e) => setFormData({...formData, email: e.target.value})}
                className="mt-1 block w-full border-2 border-slate-300 bg-slate-50 text-slate-900 rounded-md px-3 py-2 focus:border-blue-600 focus:bg-white"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700">Password</label>
              <input
                type="password"
                required
                minLength="6"
                value={formData.password}
                onChange={(e) => setFormData({...formData, password: e.target.value})}
                className="mt-1 block w-full border-2 border-slate-300 bg-slate-50 text-slate-900 rounded-md px-3 py-2 focus:border-blue-600 focus:bg-white"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700">Confirm Password</label>
              <input
                type="password"
                required
                value={formData.confirmPassword}
                onChange={(e) => setFormData({...formData, confirmPassword: e.target.value})}
                className="mt-1 block w-full border-2 border-slate-300 bg-slate-50 text-slate-900 rounded-md px-3 py-2 focus:border-blue-600 focus:bg-white"
              />
            </div>
            
            <div className="flex justify-end space-x-3 pt-4 border-t">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
              >
                {loading ? 'Creating...' : 'Create Admin'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

// Create Family Modal Component (placeholder for now)
const CreateFamilyModal = ({ onClose, onFamilyCreated }) => {
  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
        <div className="mt-3">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Create New Family</h3>
          <p className="text-gray-600 mb-4">
            Family creation is handled through the main app registration process. 
            Direct admin family creation will be available in a future update.
          </p>
          <div className="flex justify-end">
            <button
              onClick={onClose}
              className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;